import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Info, Loader2 } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
    ApiCallError, previewStatusOverride, applyStatusOverride,
    type OverridePreviewDto, type OverrideStatusCode,
} from '@/api/client';
import type { AppointmentDto } from '@/api/types';

/**
 * CHANGE AN APPOINTMENT'S STATUS BY HAND (#306 stage 3).
 *
 * Pete, 2026-08-27, having asked for a SQL script to put A00000248 back to
 * Approved: *"Or maybe make a config screen for admin's only that allows me to
 * change a status on an order. Obviously it gets logged too."*
 *
 * ---- ⛔⛔ THIS IS THE ONE DIALOG WITH NO TRANSITION RULES ------------------
 *
 * ⚠️ EVERY OTHER STATUS CONTROL IN THIS APP ENFORCES A PATH. Mark-departed runs
 * the outbound release gate; mark-arrived refuses a cancelled load. This goes
 * anywhere from anywhere — that is what makes it useful for repairing a row, and
 * it is also why it can undo the release gate with two clicks.
 *
 * ⭐ SO THE CONSEQUENCES ARE THE PRODUCT, NOT A NICETY. The dialog previews on
 * every change and refuses to enable Apply until it has an answer from the
 * server. What it must never do is let somebody click through faster than they
 * can read.
 *
 * ---- ⛔ THE CONSEQUENCES ARE THE SERVER'S WORDS ----------------------------
 *
 * ⚠️ `planOverride` lives in src/ and this lives in web/ — separate builds. A
 * second copy of the rules here would drift, and the drift would be silent: the
 * dialog would explain one thing while the server did another. So this renders
 * `plan.consequences` verbatim and styles on `code`/`level`, never on the text.
 *
 * ---- ⚠️ AND THE STAMPS ARE THE DANGEROUS PART ------------------------------
 *
 * `ActualArrivalUtc` and `ActualDepartureUtc` are claims about a physical truck,
 * and every dwell-time number downstream subtracts one from the other. The two
 * tick boxes below can erase them. ⛔ Nothing here invents a replacement — the
 * server refuses to, and says so in the consequence list.
 */

const STATUS_LABEL: Record<OverrideStatusCode, string> = {
    REQUESTED: 'Requested',
    APPROVED: 'Approved',
    ARRIVED: 'Arrived',
    DEPARTED: 'Departed',
    NOSHOW: 'No-show',
    CANCELLED: 'Cancelled',
};

/**
 * ⛔ THE LIST IS TYPED HERE AND THE SERVER RE-VALIDATES IT. That is not two
 * definitions of one fact — it is a screen offering choices and a server refusing
 * anything else. `OVERRIDE_STATUSES` cannot be imported across the build
 * boundary, and a wrong value here fails loudly at the route rather than quietly
 * anywhere.
 */
const CHOICES: OverrideStatusCode[] =
    ['REQUESTED', 'APPROVED', 'ARRIVED', 'DEPARTED', 'NOSHOW', 'CANCELLED'];

export function StatusOverrideDialog({
    appt,
    getToken,
    onClose,
    onApplied,
}: {
    appt: AppointmentDto | null;
    getToken: () => Promise<string>;
    onClose: () => void;
    /** ⭐ The caller reloads the board — the chip and the stamps have both moved. */
    onApplied: (message: string) => void;
}) {
    const [toStatus, setToStatus] = useState<OverrideStatusCode | ''>('');
    const [reason, setReason] = useState('');
    const [clearArrival, setClearArrival] = useState(false);
    const [clearDeparture, setClearDeparture] = useState(false);
    const [preview, setPreview] = useState<OverridePreviewDto | null>(null);
    const [previewing, setPreviewing] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const appointmentId = appt ? String(appt.appointmentId) : null;

    /* ⚠️ RESET ON OPEN. A dialog that remembered the last load's reason would put
       somebody else's words into this appointment's permanent audit line. */
    useEffect(() => {
        setToStatus('');
        setReason('');
        setClearArrival(false);
        setClearDeparture(false);
        setPreview(null);
        setError(null);
    }, [appointmentId]);

    const runPreview = useCallback(async () => {
        if (!appointmentId || !toStatus) { setPreview(null); return; }
        setPreviewing(true);
        try {
            setPreview(await previewStatusOverride(await getToken(), appointmentId, {
                toStatus, reason, clearArrival, clearDeparture,
            }));
            setError(null);
        } catch (err) {
            setPreview(null);
            setError(err instanceof ApiCallError
                ? err.details.error
                : 'Could not work out what that change would do.');
        } finally {
            setPreviewing(false);
        }
    }, [appointmentId, toStatus, reason, clearArrival, clearDeparture, getToken]);

    /**
     * ⭐ RE-PREVIEWED ON EVERY CHANGE, INCLUDING THE REASON. The reason affects the
     * verdict (too short is a refusal), so a preview that ignored it would show
     * "allowed" right up until Apply failed.
     *
     * ⚠️ DEBOUNCED, because this fires per keystroke in the reason box.
     */
    useEffect(() => {
        if (!toStatus) { setPreview(null); return; }
        const h = window.setTimeout(() => { void runPreview(); }, 250);
        return () => window.clearTimeout(h);
    }, [toStatus, reason, clearArrival, clearDeparture, runPreview]);

    async function doApply() {
        if (!appointmentId || !toStatus || !preview) return;
        setBusy(true);
        setError(null);
        try {
            const r = await applyStatusOverride(await getToken(), appointmentId, {
                toStatus, reason, clearArrival, clearDeparture,
                /* ⛔ WHAT THE ADMIN WAS LOOKING AT, echoed from the preview the
                   server itself produced — not a value the browser guessed. */
                expectedCurrentStatus: preview.subject.currentStatus,
            });
            onApplied(`${preview.appointmentNumber ?? 'This appointment'} is now ${STATUS_LABEL[r.to as OverrideStatusCode] ?? r.to}.`);
            onClose();
        } catch (err) {
            /* ⚠️ A STALE ROW IS NOT A VALIDATION FAILURE. The admin's input was
               fine; the world changed underneath. Re-previewing shows them the new
               starting point rather than leaving them to re-read a plan that no
               longer applies. */
            if (err instanceof ApiCallError && err.details.code === 'StatusOverrideStale') {
                setError(err.details.error);
                void runPreview();
            } else {
                setError(err instanceof ApiCallError ? err.details.error : 'Could not apply that change.');
            }
        } finally {
            setBusy(false);
        }
    }

    if (!appt) return null;

    const plan = preview?.plan ?? null;
    const subject = preview?.subject ?? null;
    /* ⛔ APPLY IS ENABLED ONLY ON A SERVER-BLESSED PLAN. Not "the fields look
       filled in" — the server has to have said yes to this exact combination. */
    const canApply = !!plan && plan.allowed && !busy && !previewing;

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Change status — {appt.appointmentNumber}</DialogTitle>
                    <DialogDescription>
                        This bypasses every normal rule, including the outbound release check.
                        It is written to the appointment history with your name and your reason.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <div className="rounded border bg-muted/40 px-2 py-1.5 text-xs">
                        <span className="text-muted-foreground">Now: </span>
                        <span className="font-medium">
                            {STATUS_LABEL[appt.statusCode as OverrideStatusCode] ?? appt.statusCode}
                        </span>
                        {/**
                          * ⭐ THE STAMPS ARE SHOWN BEFORE ANYTHING IS CHANGED. They are
                          * what the two tick boxes below can erase, and an admin should
                          * not have to guess whether there is anything there to erase.
                          */}
                        {subject && (
                            <span className="text-muted-foreground">
                                {' · arrived '}{subject.actualArrivalUtc ? new Date(subject.actualArrivalUtc).toLocaleString() : '—'}
                                {' · left '}{subject.actualDepartureUtc ? new Date(subject.actualDepartureUtc).toLocaleString() : '—'}
                            </span>
                        )}
                    </div>

                    <div>
                        <Label htmlFor="so-to">Change it to</Label>
                        <select
                            id="so-to"
                            value={toStatus}
                            onChange={(e) => setToStatus(e.target.value as OverrideStatusCode | '')}
                            className="mt-1.5 w-full rounded border px-2 py-1.5 text-sm"
                        >
                            <option value="">Choose a status…</option>
                            {CHOICES
                                .filter(c => c !== appt.statusCode)
                                .map(c => <option key={c} value={c}>{STATUS_LABEL[c]}</option>)}
                        </select>
                    </div>

                    <div>
                        <Label htmlFor="so-reason">Reason</Label>
                        <textarea
                            id="so-reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={2}
                            placeholder="Marked departed by mistake at the gate"
                            className="mt-1.5 w-full rounded border px-2 py-1.5 text-sm"
                        />
                        <p className="mt-1 text-[10px] text-muted-foreground">
                            Goes into the appointment history. Six weeks from now this is the only
                            thing that explains the change.
                        </p>
                    </div>

                    {/**
                      * ⛔ THE STAMP BOXES ARE OFF BY DEFAULT AND STAY THAT WAY. Clearing a
                      * time is destructive and unrecoverable from this screen; the default
                      * has to be the one that keeps evidence.
                      */}
                    <div className="space-y-1">
                        <label className="flex items-center gap-2 text-xs">
                            <input
                                type="checkbox"
                                checked={clearArrival}
                                disabled={!subject?.actualArrivalUtc}
                                onChange={(e) => setClearArrival(e.target.checked)}
                            />
                            Also clear the arrival time
                            {!subject?.actualArrivalUtc && (
                                <span className="text-muted-foreground">(there isn&rsquo;t one)</span>
                            )}
                        </label>
                        <label className="flex items-center gap-2 text-xs">
                            <input
                                type="checkbox"
                                checked={clearDeparture}
                                disabled={!subject?.actualDepartureUtc}
                                onChange={(e) => setClearDeparture(e.target.checked)}
                            />
                            Also clear the departure time
                            {!subject?.actualDepartureUtc && (
                                <span className="text-muted-foreground">(there isn&rsquo;t one)</span>
                            )}
                        </label>
                    </div>

                    {previewing && (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Loader2 className="h-3 w-3 animate-spin" /> Working out what that would do…
                        </div>
                    )}

                    {/**
                      * ⛔ THE REFUSAL IS SHOWN WHERE THE CONSEQUENCES WOULD BE, not as a
                      * toast after clicking. A refusal an admin only meets on submit is a
                      * refusal they meet after they have stopped reading.
                      */}
                    {plan && !plan.allowed && plan.refusal && (
                        <div className="flex items-start gap-1.5 rounded border border-destructive/40 bg-destructive/10 px-2 py-1.5 text-xs text-destructive">
                            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
                            <span>{plan.refusal}</span>
                        </div>
                    )}

                    {plan && plan.allowed && plan.consequences.length > 0 && (
                        <div className="space-y-1">
                            <div className="text-xs font-medium">What this will do:</div>
                            {plan.consequences.map(c => (
                                <div
                                    key={c.code}
                                    className={'flex items-start gap-1.5 rounded border px-2 py-1 text-xs '
                                        + (c.level === 'warn'
                                            ? 'border-amber-300 bg-amber-50 text-amber-900'
                                            : 'border-slate-200 bg-slate-50 text-slate-700')}
                                >
                                    {c.level === 'warn'
                                        ? <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
                                        : <Info className="mt-px h-3.5 w-3.5 shrink-0" />}
                                    <span>{c.text}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {error && (
                        <div className="rounded border border-destructive/40 bg-destructive/10 px-2 py-1.5 text-xs text-destructive">
                            {error}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
                        Cancel
                    </Button>
                    <Button type="button" onClick={() => void doApply()} disabled={!canApply}>
                        {busy ? 'Applying…' : 'Change the status'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
